import {
  amountSort,
  formatMoney,
  fromCents,
  type ScheduleAccount,
  type ScheduleOverrideRequest,
  type ScheduleRow,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { SortHeader, type TableSorting } from '../../components/SortHeader.js';
import { formatDate } from '../../lib/format.js';
import {
  BLOCK_LABELS,
  groupByBlock,
  isTransferRow,
  MARK_LABELS,
  type ScheduleFilters,
} from './schedule-view.js';

/** Values each column of the scadenzario sorts by. */
export const SCHEDULE_SORT_VALUES = {
  subject: (row: ScheduleRow) => row.subjectName,
  document: (row: ScheduleRow) => row.document,
  account: (row: ScheduleRow) => row.accountCode,
  dueDate: (row: ScheduleRow) => row.dueDate,
  rescheduleOn: (row: ScheduleRow) => row.rescheduleOn,
  late: (row: ScheduleRow) => row.lateDays,
  collection: (row: ScheduleRow) => (toCents(row.amount) > 0 ? amountSort(row.amount) : null),
  payment: (row: ScheduleRow) => (toCents(row.amount) < 0 ? -(amountSort(row.amount) ?? 0) : null),
  note: (row: ScheduleRow) => row.note,
};

/** Where the balance cell of a row takes its colour. */
const TONE_CLASS = {
  ok: '',
  soglia: 'schedule-tone-soglia',
  fido: 'schedule-tone-fido',
  oltre_fido: 'schedule-tone-oltre',
} as const;

function RowMarks({ row }: { row: ScheduleRow }) {
  return (
    <>
      {row.marks.map((mark) => (
        <span
          key={mark}
          className={`pill ${mark === 'nota_credito' ? 'credit-note' : mark === 'stimato' ? 'warn' : 'muted'}`}
        >
          {MARK_LABELS[mark]}
        </span>
      ))}
    </>
  );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The rescheduled date: kept as typed, saved on blur or Enter once it is a complete date. */
function DateCell({ row, onSave }: { row: ScheduleRow; onSave: (date: string | null) => void }) {
  const stored = row.rescheduleOn ?? '';
  const [draft, setDraft] = useState(stored);
  const [seen, setSeen] = useState(stored);
  if (seen !== stored) {
    setSeen(stored);
    setDraft(stored);
  }
  const commit = () => {
    if (draft === stored) return;
    if (draft === '') onSave(null);
    else if (ISO_DATE.test(draft) && !Number.isNaN(Date.parse(draft))) onSave(draft);
    else setDraft(stored);
  };
  return (
    <input
      type="date"
      className={row.rescheduleOn ? 'schedule-resched' : undefined}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit();
      }}
    />
  );
}

function NoteCell({
  row,
  editable,
  onSave,
}: {
  row: ScheduleRow;
  editable: boolean;
  onSave: (note: string | null) => void;
}) {
  if (!editable) return <>{row.note ?? ''}</>;
  return (
    <input
      key={row.note ?? ''}
      className="schedule-note"
      placeholder="nota"
      maxLength={500}
      defaultValue={row.note ?? ''}
      onBlur={(event) => {
        const value = event.target.value.trim();
        if (value !== (row.note ?? '')) onSave(value === '' ? null : value);
      }}
    />
  );
}

/**
 * The rows of the scadenzario in their blocks (3.25). With `canEdit` the
 * rescheduled date, the account and the note are changed in the cell, and the
 * selected rows in bulk; the planned transfers are changed in their own panel.
 */
export function ScheduleTable({
  rows,
  accounts,
  canEdit,
  withBalance,
  busy,
  sorting,
  filters,
  onChange,
}: {
  rows: ScheduleRow[];
  accounts: ScheduleAccount[];
  canEdit: boolean;
  withBalance: boolean;
  busy: boolean;
  sorting: TableSorting<ScheduleRow>;
  filters: ScheduleFilters;
  onChange: (request: ScheduleOverrideRequest) => Promise<boolean>;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkDate, setBulkDate] = useState('');
  const [bulkDays, setBulkDays] = useState('15');
  const [bulkAccount, setBulkAccount] = useState('');
  const groups = groupByBlock(rows);
  // Only rows still in view stay selected.
  const inView = new Set(rows.map((row) => row.key));
  const chosen = selected.filter((key) => inView.has(key));
  const columns = 9 + (canEdit ? 1 : 0) + (withBalance ? 1 : 0);

  const toggle = (keys: string[], on: boolean) =>
    setSelected((current) => [
      ...current.filter((key) => !keys.includes(key)),
      ...(on ? keys : []),
    ]);
  const apply = async (request: Omit<ScheduleOverrideRequest, 'keys'>) => {
    if (!(await onChange({ ...request, keys: chosen }))) return;
    setSelected([]);
    setBulkDate('');
    setBulkAccount('');
  };
  // The balance of all the accounts at the end of the horizon (spec 5).
  const ends = accounts
    .filter((item) => !filters.account || item.code === filters.account)
    .flatMap((item) => (item.end === null ? [] : [toCents(item.end)]));
  const endTotal = ends.length > 0 ? ends.reduce((sum, value) => sum + value, 0) : null;
  const totals = { in: 0, out: 0 };
  for (const row of rows) {
    if (row.block === 'all_erogazione') continue;
    const cents = toCents(row.amount);
    if (cents > 0) totals.in += cents;
    else totals.out -= cents;
  }
  const days = Number.parseInt(bulkDays, 10);

  return (
    <div className="stack">
      {canEdit && chosen.length > 0 && (
        <div className="card row schedule-bulk">
          <strong>{chosen.length} selezionate</strong>
          <label>
            Nuova data{' '}
            <input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
          </label>
          <button
            type="button"
            className="primary small"
            disabled={busy || bulkDate === ''}
            onClick={() => void apply({ rescheduleOn: bulkDate })}
          >
            Riprogramma
          </button>
          <span className="muted">oppure</span>
          <label>
            + giorni da oggi{' '}
            <input
              type="number"
              min={1}
              max={365}
              className="small-input"
              value={bulkDays}
              onChange={(e) => setBulkDays(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="small"
            disabled={busy || !(days >= 1 && days <= 365)}
            onClick={() => void apply({ shiftDays: days })}
          >
            Sposta
          </button>
          <span className="muted">·</span>
          <label>
            Conto{' '}
            <select value={bulkAccount} onChange={(e) => setBulkAccount(e.target.value)}>
              <option value="">Scegli…</option>
              {accounts.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.alias}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="small"
            disabled={busy || bulkAccount === ''}
            onClick={() => void apply({ accountCode: bulkAccount })}
          >
            Cambia conto
          </button>
          <button type="button" className="link" onClick={() => setSelected([])}>
            Deseleziona
          </button>
        </div>
      )}
      <div className="table-wrap card flush">
        <table className="data-table schedule-table">
          <thead>
            <tr>
              {canEdit && <th aria-label="Selezione" />}
              <SortHeader sort={sorting} column="subject">
                Anagrafica
              </SortHeader>
              <SortHeader sort={sorting} column="document">
                Documento
              </SortHeader>
              <SortHeader sort={sorting} column="account">
                Conto
              </SortHeader>
              <SortHeader sort={sorting} column="dueDate">
                Scadenza
              </SortHeader>
              <SortHeader sort={sorting} column="rescheduleOn">
                Riprogrammata
              </SortHeader>
              <SortHeader sort={sorting} column="late" numeric>
                Ritardo
              </SortHeader>
              <SortHeader sort={sorting} column="collection" numeric>
                Incasso
              </SortHeader>
              <SortHeader sort={sorting} column="payment" numeric>
                Pagamento
              </SortHeader>
              {withBalance && <th className="numeric">Saldo previsto</th>}
              <SortHeader sort={sorting} column="note">
                Nota
              </SortHeader>
            </tr>
          </thead>
          <tbody>
            {groups.map(({ block, rows: groupRows }) => {
              const selectable = canEdit
                ? groupRows.filter((row) => !isTransferRow(row)).map((row) => row.key)
                : [];
              const allOn =
                selectable.length > 0 && selectable.every((key) => chosen.includes(key));
              return [
                <tr
                  key={`group-${block}`}
                  className={`schedule-group${block === 'da_riprogrammare' ? ' alarm' : ''}`}
                >
                  <td colSpan={columns}>
                    {selectable.length > 0 && (
                      <input
                        type="checkbox"
                        aria-label={`Seleziona tutte: ${BLOCK_LABELS[block]}`}
                        checked={allOn}
                        onChange={(event) => toggle(selectable, event.target.checked)}
                      />
                    )}{' '}
                    {BLOCK_LABELS[block]} ({groupRows.length})
                  </td>
                </tr>,
                ...groupRows.map((row) => {
                  const transfer = isTransferRow(row);
                  const editable = canEdit && !transfer;
                  const cents = toCents(row.amount);
                  const tone = row.tone ? TONE_CLASS[row.tone] : '';
                  return (
                    <tr
                      key={row.key}
                      className={row.marks.includes('nota_credito') ? 'credit-note-row' : undefined}
                    >
                      {canEdit && (
                        <td>
                          {!transfer && (
                            <input
                              type="checkbox"
                              aria-label={`Seleziona ${row.subjectName}`}
                              checked={chosen.includes(row.key)}
                              onChange={(event) => toggle([row.key], event.target.checked)}
                            />
                          )}
                        </td>
                      )}
                      <td>
                        {row.subjectName} <RowMarks row={row} />
                      </td>
                      <td>{row.link ? <Link to={row.link}>{row.document}</Link> : row.document}</td>
                      <td>
                        {editable && row.accountCode !== null ? (
                          <select
                            className="compact-select"
                            value={row.accountCode}
                            disabled={busy}
                            onChange={(event) =>
                              void onChange({ keys: [row.key], accountCode: event.target.value })
                            }
                          >
                            {accounts.map((item) => (
                              <option key={item.code} value={item.code}>
                                {item.alias}
                              </option>
                            ))}
                          </select>
                        ) : (
                          (accounts.find((item) => item.code === row.accountCode)?.alias ??
                          row.accountCode ??
                          '—')
                        )}
                      </td>
                      <td>
                        {row.dueDate ? (
                          formatDate(row.dueDate)
                        ) : (
                          <span className="muted">all’erogazione</span>
                        )}
                      </td>
                      <td>
                        {editable ? (
                          <DateCell
                            row={row}
                            onSave={(date) =>
                              void onChange({ keys: [row.key], rescheduleOn: date })
                            }
                          />
                        ) : row.rescheduleOn ? (
                          <span className="schedule-resched">{formatDate(row.rescheduleOn)}</span>
                        ) : null}
                      </td>
                      <td className="numeric">
                        {row.lateDays === null ? (
                          <span className="muted">—</span>
                        ) : row.lateDays > 0 ? (
                          <span className="error">{row.lateDays} gg</span>
                        ) : (
                          <span className="muted">tra {-row.lateDays} gg</span>
                        )}
                      </td>
                      <td className="numeric schedule-in">
                        {cents > 0 ? formatMoney(row.amount) : ''}
                      </td>
                      <td className="numeric">{cents < 0 ? formatMoney(fromCents(-cents)) : ''}</td>
                      {withBalance && (
                        <td className={`numeric ${tone}`}>
                          {row.balance !== null && row.accountCode
                            ? `${row.accountCode} ${formatMoney(row.balance)}`
                            : ''}
                        </td>
                      )}
                      <td>
                        <NoteCell
                          row={row}
                          editable={editable}
                          onSave={(note) => void onChange({ keys: [row.key], note })}
                        />
                      </td>
                    </tr>
                  );
                }),
              ];
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns} className="muted">
                  Nessuna scadenza con questi filtri
                  {filters.side !== 'all' ? ' su questo lato' : ''}.
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={6 + (canEdit ? 1 : 0)}>
                  Totale delle righe in vista (senza quelle all’erogazione)
                </td>
                <td className="numeric schedule-in">{formatMoney(fromCents(totals.in))}</td>
                <td className="numeric">{formatMoney(fromCents(totals.out))}</td>
                {withBalance && (
                  <td className="numeric">
                    {endTotal === null ? '' : `Totale conti ${formatMoney(fromCents(endTotal))}`}
                  </td>
                )}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
